/**
 * Meta Pixel (Facebook Pixel) 유틸리티 함수
 */

// Meta Pixel 파라미터 타입
type MetaPixelParams = Record<string, string | number | boolean | string[] | undefined>;

declare global {
  interface Window {
    fbq: (
      action: string,
      eventName: string,
      params?: MetaPixelParams
    ) => void;
    _fbq: unknown;
  }
}

/**
 * Meta Pixel 이벤트 전송
 */
export function sendMetaPixelEvent(
  eventName: string,
  params?: MetaPixelParams
): boolean {
  if (typeof window !== 'undefined' && window.fbq) {
    if (params) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[Meta Pixel] Event sent:', eventName, params);
    }

    return true;
  }

  return false;
}

/**
 * Meta Pixel 커스텀 이벤트 전송
 */
export function sendMetaPixelCustomEvent(
  eventName: string,
  params?: MetaPixelParams
): boolean {
  if (typeof window !== 'undefined' && window.fbq) {
    if (params) {
      window.fbq('trackCustom', eventName, params);
    } else {
      window.fbq('trackCustom', eventName);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[Meta Pixel] Custom event sent:', eventName, params);
    }

    return true;
  }

  return false;
}

// 표준 이벤트 헬퍼 함수들

/**
 * 구매 완료 이벤트
 */
export function trackPurchase(params: {
  value: number;
  currency: string;
  content_name?: string;
  content_ids?: string[];
  content_type?: string;
  num_items?: number;
}): boolean {
  return sendMetaPixelEvent('Purchase', params);
}

/**
 * 리드 생성 이벤트 (잠재 고객)
 */
export function trackLead(params?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}): void {
  sendMetaPixelEvent('Lead', params);
}

/**
 * 회원가입 완료 이벤트
 */
export function trackCompleteRegistration(params?: {
  content_name?: string;
  status?: string;
  value?: number;
  currency?: string;
}): void {
  sendMetaPixelEvent('CompleteRegistration', params);
}

/**
 * 콘텐츠 조회 이벤트
 */
export function trackViewContent(params?: {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}): void {
  sendMetaPixelEvent('ViewContent', params);
}

// localStorage 키
const META_PIXEL_REGISTRATION_SENT_KEY = 'meta_pixel_registration_sent';
const META_PIXEL_5_PROBLEMS_SENT_KEY = 'meta_pixel_5_problems_sent';

/**
 * 회원가입 픽셀 이벤트 전송 여부 확인
 */
export function hasMetaRegistrationSent(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(META_PIXEL_REGISTRATION_SENT_KEY) === 'true';
}

/**
 * 회원가입 픽셀 이벤트 전송 완료 표시
 */
export function markMetaRegistrationSent(): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(META_PIXEL_REGISTRATION_SENT_KEY, 'true');
  }
}

/**
 * 5개 문제 풀이 픽셀 이벤트 전송 여부 확인
 */
export function hasMetaPixel5ProblemsSent(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(META_PIXEL_5_PROBLEMS_SENT_KEY) === 'true';
}

/**
 * 5개 문제 풀이 픽셀 이벤트 전송 완료 표시
 */
export function markMetaPixel5ProblemsSent(): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(META_PIXEL_5_PROBLEMS_SENT_KEY, 'true');
  }
}
